import type {
  DisplayOverride,
  FontFamilyPref,
  LetterSpacing,
  LineHeight,
  ThemeMode,
} from '@/lib/store/themeStore';

// `accessibility` is a SIBLING of `accommodations`: a student control must never reach the teacher-owned record.
export interface AccessibilityPrefs {
  theme?: ThemeMode;
  font_family?: FontFamilyPref;
  /** null = Reset. Not deleteField(): it would ride the same payload into the Supabase upsert and Dexie, which cannot express it. */
  font_scale?: number | null;
  letter_spacing?: LetterSpacing;
  line_height?: LineHeight;
}

/** Structural: pages/experiences may not import `AccommodationData` from pages/users (three-layer law). */
export interface ReadingPrefAccommodations {
  dyslexia_font_enabled?: boolean;
  dyslexia_font?: string;
}

/** Display names as the grant stores them (AccommodationDialog's DYSLEXIA_FONTS). */
const FONT_NAME_BY_PREF: Record<Exclude<FontFamilyPref, 'default'>, string> = {
  'lexie-readable': 'Lexie Readable',
  'open-dyslexic': 'Open-Dyslexic',
  'sf-pro-display': 'SF Pro Display',
};

const PREF_BY_FONT_NAME: Record<string, FontFamilyPref> = Object.fromEntries(
  Object.entries(FONT_NAME_BY_PREF).map(([pref, name]) => [name, pref as FontFamilyPref])
);

export interface ReadingPrefsPatch {
  theme?: ThemeMode;
  fontScale?: number | null;
  letterSpacing?: LetterSpacing;
  lineHeight?: LineHeight;
  fontFamily?: FontFamilyPref;
}

/** Undefined, never 'default': 'default' would read as a real student choice instead of "next tier decides". */
export const fontFamilyPrefFromAccommodations = (
  accommodations: ReadingPrefAccommodations | undefined
): FontFamilyPref | undefined => {
  if (!accommodations?.dyslexia_font_enabled) return undefined;
  const name = accommodations.dyslexia_font;
  return name ? PREF_BY_FONT_NAME[name] : undefined;
};

/** Reset's null becomes `undefined`: a field must be ABSENT to fall through to the device Display pref. */
export const displayOverridePatchFromReadingPrefs = (
  patch: ReadingPrefsPatch
): Partial<DisplayOverride> => {
  const next: Partial<DisplayOverride> = {};
  if (patch.theme !== undefined) next.theme = patch.theme;
  if (patch.fontScale !== undefined) next.fontScale = patch.fontScale ?? undefined;
  if (patch.letterSpacing !== undefined) next.letterSpacing = patch.letterSpacing;
  if (patch.lineHeight !== undefined) next.lineHeight = patch.lineHeight;
  if (patch.fontFamily !== undefined) next.fontFamily = patch.fontFamily;
  return next;
};

/** Emits only changed fields: the write merges per leaf, so an untouched control must stay off the wire. */
export const accessibilityPatchFromReadingPrefs = (
  patch: ReadingPrefsPatch
): AccessibilityPrefs => {
  const next: AccessibilityPrefs = {};
  if (patch.theme !== undefined) next.theme = patch.theme;
  if (patch.fontScale !== undefined) next.font_scale = patch.fontScale;
  if (patch.letterSpacing !== undefined) next.letter_spacing = patch.letterSpacing;
  if (patch.lineHeight !== undefined) next.line_height = patch.lineHeight;
  if (patch.fontFamily !== undefined) next.font_family = patch.fontFamily;
  return next;
};
